/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { AppContainer, Col, UiBox, UiDivider, UiHeading, UiLink, UiText } from "~/components/elements";
// eslint-disable-next-line no-restricted-imports
import { BoxProps, HeadingProps, ListItem, OrderedList, TextProps, UnorderedList } from "@chakra-ui/react";
import { useAppSelector, useScrollToTop } from "~/hooks";
import { Link } from "react-router-dom";
import { PageLayout } from "~/components/layouts/PageLayout";
import { PublicLayout } from "~/components/layouts";

function Header(p: HeadingProps) {
  return <UiHeading size="md" {...p} />;
}

function Section(p: BoxProps) {
  return <UiBox mt={8} mb={3} {...p} />;
}

function Content(p: BoxProps) {
  return <UiBox mt={4} mb={3} {...p} />;
}

function Paragraph(p: TextProps) {
  return <UiText fontSize="lg" mt={4} mb={3} {...p} />;
}

function PrivacyPolicyPage() {
  const menuHeight = useAppSelector(state => state.ui.menu.height);

  useScrollToTop();

  return (
    <PublicLayout>
      <PageLayout
        metaTagsConfig={{
          title: `Bettershot - Privacy Policy`,
          description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
          charset: "utf8",
          lang: "en",
          metas: [
            {
              name: "keywords",
              content: "",
            },
            { name: "robots", content: "index, follow" },
          ],
          links: [{ rel: "canonical", href: window.location.href }],
        }}
      >
        <Col px="20px" align="center" mb={10} bg="#84A7B6" pt={`${menuHeight + 40}px`} pb={20} color="white">
          <UiHeading alignContent="center" as="h1" size="3xl" mb={4}>
            Privacy Policy
          </UiHeading>
          <UiText align="center" width={{ base: "90%", lg: "70%" }}>
            At Bettershot, your privacy and the protection of your data is our top priority. All data collected, both
            personal and non-personal, is protected with the highest global standards.
          </UiText>
          <UiDivider width="70%" />
          <UiText align="center" width={{ base: "90%", lg: "70%" }}>
            Bettershot is operated by Bettershot LLC Pte Ltd. At Bettershot, your privacy and the protection of your
            data is our top priority. All data collected, both personal and non-personal, is protected with the highest
            global standards. As we believe your personal data is and should always remain yours, we have a strong
            privacy policy in place. This policy is fully compliant with the European General Data Protection Regulation
            (GDPR), as well as the related laws and regulations in place at Singapore.
          </UiText>
          <UiText align="center" width={{ base: "90%", lg: "70%" }}>
            The contents of this privacy policy are applicable to the website{" "}
            <UiLink as={Link} to="www.bettershot.ai">
              www.bettershot.ai
            </UiLink>{" "}
            and use of the services provided by this site to the user.
          </UiText>
        </Col>
        <AppContainer px="20px">
          <Section>
            <Header>TABLE OF CONTENTS</Header>
            <Content>
              <UiBox ml={7}>
                <OrderedList fontSize={{ base: "sm", lg: "lg" }}>
                  <ListItem>NOTICE</ListItem>
                  <ListItem>YOUR CONSENT</ListItem>
                  <ListItem>USAGE</ListItem>
                  <ListItem>RETENTION OF INFOMATION</ListItem>
                  <ListItem>YOUR RIGHTS</ListItem>
                  <ListItem>OPTING OUT</ListItem>
                  <ListItem>SECURITY OF YOUR INFORMATION</ListItem>
                  <ListItem>CHANGES TO THIS POLICY</ListItem>
                  <ListItem>CONTACT</ListItem>
                </OrderedList>
              </UiBox>
            </Content>
          </Section>
          <Section>
            <Header>1. NOTICE</Header>
            <Content>
              <Paragraph>
                Whenever we request information that can be used to personally identify you, you will explicitly be
                asked to provide it and/or give us permission to use it. This information is requested by our system
                whenever confirmation of your permission is required to continue (e.g. asking for permission for the use
                of cookies when you visit our site, asking for your payment information when you sign up).
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>2. YOUR CONSENT</Header>
            <Content>
              <Paragraph>
                You will always have the choice whether to give us permission to process your information or not.
              </Paragraph>
              <Paragraph>
                With data that is not essential to the core functionality of our services (e.g. cookies for marketing
                purposes, company name, etc.) you are able to decline permission without any consequences to your access
                to Bettershot.
              </Paragraph>
              <Paragraph>
                If you choose to decline the use of essential information for the use of our services (e.g. essential
                cookies, payment information, e-mail address), we will be unable to give you access to all aspects of
                Bettershot that require such information to function. Whether any given information is essential to our
                services or not will be explicitly indicated when you are asked to provide or give permission for us to
                use such information.
              </Paragraph>
              <Paragraph>
                By agreeing to this privacy policy and providing us with your personal information you consent to us
                processing said information as described in this privacy policy.
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>3. USAGE</Header>
            <Content>
              <Header>3.1. Data we collect</Header>
              <Paragraph>
                We collect various forms of personal identifiable information. This includes all information which could
                directly be used to identify you. This includes your name, e-mail address, payment information and more.
              </Paragraph>
              <Paragraph>
                We also collect data based on your usage of our platform with our optional cookies. This data is
                collected and processed fully anonymously, meaning it can not be traced back to you or your person.
              </Paragraph>
              <Header>3.2. How we use your data</Header>
              <Paragraph>We use your personally identifiable information for the following purposes:</Paragraph>
              <UnorderedList fontSize={{ base: "sm", lg: "lg" }}>
                <ListItem>Provide you with access to all of Bettershot’s features</ListItem>
                <ListItem>Process your payments</ListItem>
                <ListItem>
                  Inform you about important information regarding Bettershot, as well as any other information you
                  choose to opt in to.
                </ListItem>
                <ListItem>
                  Contact you to inform you about the status of your order, if there is a problem with your order, if
                  you request us to via our support services, or for other reasons you may opt in to.
                </ListItem>
              </UnorderedList>
              <Paragraph>
                We use anonymous usage data to analyze the use of our platform by our users so we can improve our
                services.
              </Paragraph>
              <Paragraph>
                Data regarding the use of our platform by our users may be shared with third parties we work with to
                improve our services and keep our activities running. This includes organizations like marketing
                agencies. This data will always be made fully anonymous before it’s shared.
              </Paragraph>
              <Paragraph>
                Some of your personal identifiable information may also need to be shared with our partners to provide
                you with our essential services. Examples include shipping companies when we need to physically mail you
                a product. In these cases, these partners will only be provided with the information absolutely
                necessary to provide their service.
              </Paragraph>
              <Paragraph>
                If a legal request for your personally identifiable information or other information regarding you or
                your person is requested by a federal court, judge or law enforcement, we are legally required to
                provide them with your personal identifiable information. In these cases, we may do so without your
                permission and without informing you.
              </Paragraph>
              <Paragraph>
                If a legal request for your personally identifiable information or other information regarding you or
                your person is requested by a federal court, judge or law enforcement, we are legally required to
                provide them with your personal identifiable information. In these cases, we may do so without your
                permission and without informing you.
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>4. RETENTION OF INFORMATION</Header>
            <Content>
              <Paragraph>
                We retain your information as long as this is necessary to provide you with our services. All personal
                identifiable information will be deleted when you inform us you wish to stop using our services (e.g. by
                deleting your account), when your subscription expires, or when you explicitly request us to delete it.
                Once your request has been submitted and confirmed, your information will be deleted within 14 days.
              </Paragraph>
              <Paragraph>
                Information may be retained for longer if this is necessary for legal requirements. If required by law
                or ordered by a federal judge or court, we may retain information for longer and/or share it with said
                organization.
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>5. YOUR RIGHTS</Header>
            <Content>
              <Header>5.1. Your rights</Header>
              <Paragraph>
                You are the owner of your information. As stated by the GDPR, you have several rights regarding the
                treatment of your information by us.
              </Paragraph>
              <UnorderedList fontSize={{ base: "sm", lg: "lg" }}>
                <ListItem>
                  Right of access: You are entitled to receiving a copy of all personally identifiable information
                  related to you and your person that we have processed
                </ListItem>
                <ListItem>
                  Right to rectification: You are entitled to have any personally identifiable information you have
                  provided to us corrected or adjusted in the case the information is not correct
                </ListItem>
                <ListItem>
                  Right to erasure: You are entitled to having some or all personally identifiable information we have
                  gathered be erased at your request
                </ListItem>
                <ListItem>
                  Right to restriction of processing: You are entitled to us stopping the processing of relevant
                  personally identifiable information if this information is not correct, unlawful, no longer necessary
                  according to the information in our privacy policy and/or when you object to us processing said
                  information. Objection is only valid if there is no essential reason for us to process said
                  information.
                </ListItem>
                <ListItem>
                  Right to data portability: You have the right to transfer personally identifiable information we have
                  processed to another party
                </ListItem>
              </UnorderedList>
              <Paragraph>
                If you would like to make use of any of these rights, you may contact us via the e-mail address at the
                end of this privacy policy.
              </Paragraph>
              <Header>5.2. Right to object</Header>
              <Paragraph>
                We are legally required to separately inform you about your right to object. Conform the European GDPR,
                you have the right to object to us processing your personally identifiable information for direct
                marketing purposes. If you file an objection with us, we are legally required to immediately halt the
                processing of said information for said purpose. You can file an objection using the e-mail address at
                the end of this privacy policy.
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>6. OPTING OUT</Header>
            <Content>
              <Paragraph>
                In addition to our core services, we provide various extra services to improve your experience. This
                includes the sending of newsletters, contacting you for customer satisfaction surveys, et cetera. At any
                point, you may opt out of these kinds of services. For our contact via e-mail, you can click the link at
                the bottom of any of our e-mails to change your preferences. You can also opt out of additional
                communication and services in your account settings.
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>7. SECURITY OF YOUR INFORMATION</Header>
            <Content>
              <Paragraph>
                We take various measures to protect your data to the best of our abilities. In order to achieve this, we
                make use of:
              </Paragraph>
              <UnorderedList fontSize={{ base: "sm", lg: "lg" }}>
                <ListItem>Security software, including virus scanners</ListItem>
                <ListItem>TLS secured connections for a secure connection between our servers and your device</ListItem>
                <ListItem>The DKIM, SPF and DMARC internet standards to protect our email traffic</ListItem>
              </UnorderedList>
            </Content>
          </Section>
          <Section>
            <Header>8. CHANGES TO THIS POLICY</Header>
            <Content>
              <Paragraph>
                We reserve the right to make changes to our privacy policy. When this happens, you will be notified at
                least two weeks in advance of the changes taking effect. If you do not agree with the new policy, you
                are free to withdraw your consent within the given time period.
              </Paragraph>
            </Content>
          </Section>
          <Section>
            <Header>9. CONTACT</Header>
            <Content>
              <Paragraph>
                If you have any questions about our privacy policy, have a comment about it, wish to talk about the
                enforcement of our policy or want to otherwise discuss our privacy policy, you may reach out to us via
                the following e-mail address: hello@bettershot.ai
              </Paragraph>
            </Content>
          </Section>
        </AppContainer>
      </PageLayout>
    </PublicLayout>
  );
}

export default PrivacyPolicyPage;
